import { useEffect, useState } from "react";

import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { PlusIcon } from "lucide-react";

import { useThrottle } from "simplydo/react/hooks";
import { useTheme } from "simplydo/react/theme";
import { ButtonLink } from "simplydo/react/ui/button";
import { Container } from "simplydo/react/ui/container";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H1, H2, JustifiedHeader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Loader } from "simplydo/react/ui/loader";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import GroupCard, { GroupCardListView } from "components/lib/Cards/GroupCard";
import { OrgSwitcherDropdown } from "components/lib/OrgSwitcherTray";
import { EmptyBox } from "components/lib/UI";

import type { GroupListItem } from "./types";

const Groups = () => {
  const [yourGroups, setYourGroups] = useState<GroupListItem[]>([]);
  const [discoverGroups, setDiscoverGroups] = useState<GroupListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const theme = useTheme();
  const { t } = useTranslation();
  const user = useCurrentUser();

  const getGroups = useThrottle(
    () => {
      api.journey.record("groups", null, "viewGroupsPage");
      setLoading(true);
      const searchFilter = (g: GroupListItem) => {
        const groupName = (g.name || "").toLowerCase();
        return !searchInput || groupName.includes(searchInput.toLowerCase());
      };
      api.groups.discover(
        (groups) => {
          setYourGroups(groups.filter((g) => searchFilter(g) && g.isInGroup));
          setDiscoverGroups(
            groups.filter((g) => searchFilter(g) && !g.isInGroup),
          );
          setLoading(false);
        },
        (err) => {
          toast.error(err.message);
          setLoading(false);
        },
      );
    },
    400,
    [searchInput],
    { useLeadingCall: true },
  );

  useEffect(() => {
    getGroups();
  }, [getGroups, user?.ownerOrganisation?._id]);

  const ownerOrg = user?.ownerOrganisation;
  const ownerOrgs = user?.ownerOrganisations;

  return (
    <div>
      <Helmet title={t("common:capitalise", { key: "generic.groups" })} />
      <div style={{ marginTop: "2rem" }}>
        <Container>
          <JustifiedHeader>
            <div>
              <H1
                style={{
                  color: "#3b3b3b",
                  margin: "0px",
                  textTransform: "capitalize",
                }}
              >
                {t("generic.groups")}
              </H1>
              {ownerOrgs && ownerOrgs.length > 1 && ownerOrg ? (
                <div
                  style={{
                    fontSize: "0.9rem",
                    display: "flex",
                    alignItems: "baseline",
                    gap: "0.25em",
                  }}
                >
                  <span>
                    in <span style={{ fontWeight: 600 }}>{ownerOrg.name}</span>
                    {" -"}
                  </span>
                  <OrgSwitcherDropdown
                    basic
                    trigger={
                      <a
                        tabIndex={0}
                        role="button"
                        onClick={(e) => e.preventDefault()}
                      >
                        Change organisation
                      </a>
                    }
                  />
                </div>
              ) : null}
            </div>
            {ownerOrg &&
              util.hasPermission(user, "org.createGroups", ownerOrg._id) && (
                <ButtonLink
                  size={theme?.sizes?.isMobile ? "small" : "medium"}
                  to="/groups/new"
                >
                  <Icon icon={PlusIcon} />
                  {t("groups.discover.create")}
                </ButtonLink>
              )}
          </JustifiedHeader>
          <Input
            value={searchInput}
            placeholder={t("groups.search")}

            style={{ width: 370 }}
            onChange={(_e, { value }) => setSearchInput(value)}
          />
        </Container>
      </div>
      <Container style={{ marginTop: "1rem" }}>
        {loading && <Loader />}

        {!loading && !yourGroups.length && !discoverGroups.length ? (
          <EmptyBox>
            <H2>
              {searchInput.length
                ? t("groups.groups.noneTerm")
                : t("groups.groups.none")}
            </H2>
          </EmptyBox>
        ) : null}
        <Grid
          style={{ marginTop: "1rem" }}
          columns={
            yourGroups.length && discoverGroups.length && theme.sizes.isComputer
              ? 2
              : 1
          }
        >
          {yourGroups.length ? (
            <GridColumn>
              <H2>{t("groups.groups.member")}</H2>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                }}
              >
                {yourGroups.map((group) => (
                  <GroupCardListView key={group._id} group={group} />
                ))}
              </div>
            </GridColumn>
          ) : null}
          {discoverGroups.length ? (
            <GridColumn
              style={!theme.sizes.isComputer ? { marginTop: 20 } : {}}
            >
              <H2>{t("groups.discover.title")}</H2>
              <Grid columns={yourGroups.length > 0 ? 2 : 4} mobile={1}>
                {discoverGroups.map((group) => (
                  <GridColumn key={group._id}>
                    <GroupCard group={group} />
                  </GridColumn>
                ))}
              </Grid>
            </GridColumn>
          ) : null}
        </Grid>
      </Container>
    </div>
  );
};

export default Groups;
